import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"

export const useUserProfile = () => {
  const { user, isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0()
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const apiUrl = useMemo(() => process.env.REACT_APP_API_URL, [])
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const fetchProfile = useCallback(async () => {
    if (isLoading) {
      if (isMountedRef.current) {
        setLoading(true)
      }
      return
    }

    if (!isAuthenticated || !user) {
      if (isMountedRef.current) {
        setProfile(null)
        setError(null)
        setLoading(false)
      }
      return
    }

    if (!apiUrl) {
      console.error("API URL is not configured.")
      if (isMountedRef.current) {
        setError("Profile service is unavailable.")
        setLoading(false)
      }
      return
    }

    if (isMountedRef.current) {
      setLoading(true)
      setError(null)
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })

      const sanitizedUserId = user?.sub?.replace(/\|/g, "").replace(/-/g, "")

      if (!sanitizedUserId) {
        throw new Error("Unable to determine user identifier.")
      }

      const response = await fetch(`${apiUrl}/api/users/${sanitizedUserId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to load profile (${response.status})`)
      }

      const data = await response.json()

      if (isMountedRef.current) {
        setProfile(data)
      }
    } catch (err) {
      console.error("Error fetching profile:", err)
      if (isMountedRef.current) {
        setError("We couldn't load your profile right now. Please try again later.")
        setProfile(null)
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [apiUrl, getAccessTokenSilently, isAuthenticated, isLoading, user])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  return { profile, loading, error, refreshProfile: fetchProfile }
}

export default useUserProfile
